import React, { useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import './reservedTimeGauge.scss';

import ReservationInsightsFilter from '../../interfaces/reservationInsightsFilter';
import HistoricReservationService from '../historicReservationService';
import { ReservedTimeResponse } from '../../interfaces/responses/reservedTimeResponse';
import LanguageProvider from '@/providers/languageProvider';
import Colors from '@/styles/colors';
import ChartUtils from './chartUtils';

interface IReservedTimeGaugeProps {
    id: string;
    filter: ReservationInsightsFilter;
    title: string;
}

const ReservedTimeGauge: React.FC<IReservedTimeGaugeProps> = (props) => {
    const [reservedTime, setReservedTime] = useState<ReservedTimeResponse>();

    const historicReservationService = new HistoricReservationService();

    useEffect(() => {
        setReservedTime(undefined);
        historicReservationService.getPlaceReservationsReservedtime(props.filter).then((response) => {
            setReservedTime(response);
        });
    }, [props.filter.triggerUpdate]);

    function formatPercentage(value: number | undefined): number {
        return Math.round((value ?? 0));
    }

    return (
        <>
            <div id={props.id} className="gereserveerde-tijd">
                <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions(props.title, formatPercentage(reservedTime?.reservedTimePercentage))}
                />
            </div>
            <div className="d-flex justify-content-center">
                <div>
                    <span><h6>{LanguageProvider.getTranslation("pages.realestateutilization.charts.reservedtimechart.reservedplaces")}</h6></span>
                    <span>{`${LanguageProvider.getTranslation("pages.realestateutilization.charts.reservedtimechart.reservedplacesaverage")} ${reservedTime?.averagePlacesCount ?? "-"} ${LanguageProvider.getTranslation("pages.realestateutilization.charts.reservedtimechart.of")} ${reservedTime?.totalPlacesCount ?? '-'}`}</span>
                </div>
            </div>
        </>
    );
};

export default ReservedTimeGauge;

const chartOptions = (title: string, percentage: number): Highcharts.Options => ({
    chart: {
        type: 'gauge',
        plotBorderWidth: 0,
        plotShadow: false,
        height: '100%'
    },
    title: { ...ChartUtils.getTitle(title), align: 'left', x: 0 },
    pane: {
        startAngle: -135,
        endAngle: 135,
        background: [{
            outerRadius: '103%',
            innerRadius: '101%',
            backgroundColor: Colors.black,
            borderWidth: 1,
            borderColor: Colors.black
        }],
        center: ['50%', '50%'],
        size: '100%'
    },
    yAxis: {
        min: 0,
        max: 100,
        tickPixelInterval: 72,
        tickPosition: 'inside',
        tickColor: Highcharts.defaultOptions.chart?.backgroundColor || Colors.white,
        tickLength: 20,
        tickWidth: 2,
        minorTickInterval: 5,
        minorTickLength: 10,
        minorTickPosition: 'inside',
        minorTickWidth: 1,
        minorTickColor: Highcharts.defaultOptions.chart?.backgroundColor || Colors.white,
        tickPositions: [0, 25, 50, 75, 100],
        labels: {
            distance: -35,
            style: {
                fontSize: '14px'
            }
        },
        lineWidth: 0,
        plotBands: [{
            from: 0,
            to: 25,
            color: Colors.blue_cruise,
            thickness: 20
        }, {
            from: 25,
            to: 75,
            color: Colors.shakespeare_blue,
            thickness: 20
        }, {
            from: 75,
            to: 100,
            color: Colors.blue_astral,
            thickness: 20
        }]
    },
    series: [{
        type: 'gauge',
        name: title,
        data: [percentage],
        tooltip: {
            valueSuffix: '%'
        },
        dataLabels: {
            format: '{y}%',
            borderWidth: 0,
            color: (
                Highcharts.defaultOptions.title?.style?.color
            ) || Colors.black,
            style: {
                fontSize: '24px'
            }
        },
        dial: {
            radius: '95%',
            backgroundColor: Colors.royal_blue,
            baseWidth: 12,
            baseLength: '0%',
            rearLength: '0%'
        },
        pivot: {
            backgroundColor: Colors.royal_blue,
            radius: 6
        }
    }],
    tooltip: {
        formatter: function () {
            return `<b>${this.key}</b>: ${this.y}%`;
        }
    },
    credits: {
        enabled: false
    },
    accessibility: {
        enabled: false
    },
    lang: ChartUtils.getLanguageOptions(),
    exporting: ChartUtils.getExportingOptions(),
});
